import React, { useState } from 'react';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { api, AttachmentDto } from '../../services/api';

interface AttachmentListProps {
  promptId: number;
  attachments: AttachmentDto[];
  isOwner: boolean;
  isAuthenticated: boolean;
  onAttachmentDeleted: (attachmentId: number) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({
  promptId,
  attachments,
  isOwner,
  isAuthenticated,
  onAttachmentDeleted,
}) => {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDownload = async (attachment: AttachmentDto) => {
    if (!isAuthenticated) return;
    setDownloadingId(attachment.id);
    try {
      const sasDto = await api.getAttachmentSasUrl(promptId, attachment.id);
      window.open(sasDto.sasUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Failed to get download link');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (attachmentId: number) => {
    setDeletingId(attachmentId);
    try {
      await api.deleteAttachment(promptId, attachmentId);
      onAttachmentDeleted(attachmentId);
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to delete attachment');
    } finally {
      setDeletingId(null);
    }
  };

  if (attachments.length === 0) return null;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-2">Attachments</h4>
      <ul className="space-y-1" aria-label="Attachments">
        {attachments.map(att => (
          <li key={att.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-2">
            <PaperClipIcon className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
            {isAuthenticated ? (
              <button
                onClick={() => handleDownload(att)}
                disabled={downloadingId === att.id}
                className="flex-1 text-left text-blue-600 hover:underline truncate disabled:opacity-50"
                title={att.originalFileName}
              >
                {att.originalFileName}
              </button>
            ) : (
              <span className="flex-1 truncate text-gray-700" title={att.originalFileName}>
                {att.originalFileName}
              </span>
            )}
            <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(att.sizeBytes)}</span>
            {isOwner && (
              <button
                onClick={() => handleDelete(att.id)}
                disabled={deletingId === att.id}
                className="text-red-500 hover:text-red-700 flex-shrink-0 disabled:opacity-50"
                title="Remove attachment"
                aria-label={`Delete attachment ${att.originalFileName}`}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AttachmentList;
